/**
 * KI-Entwürfe (Agent Draft) Service
 * Cloud/Cockpit agent'ın hazırladığı bekleyen taslakların review inbox'ı.
 */
import api from '../lib/api';

export interface AgentDraft {
  id: string;
  ticket_number: string;
  subject: string;
  status: string;
  priority: string;
  agent_draft_reply: string;
  agent_draft_at: string;
  agent_draft_confidence: 'high' | 'medium' | 'low' | null;
  agent_draft_category: string | null;
  customer_email: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  is_stale: boolean;
  /** Team hat selbst geantwortet / Ticket erledigt — Entwurf ungenutzt, nur bestätigen. */
  is_manual?: boolean;
  ticket_type?: string | null;
  parent_ticket_id?: string | null;
  parent_ticket_number?: string | null;
  parent_subject?: string | null;
  parent_customer_email?: string | null;
  external_party_email?: string | null;
  external_type?: string | null;
  attachment_ids?: string[] | null;
  internal_target_org_id?: string | null;
  internal_target_org_name?: string | null;
}

export const agentDraftsService = {
  async list(): Promise<AgentDraft[]> {
    const { data } = await api.get('/agent-drafts');
    return data?.data || [];
  },
  async count(): Promise<number> {
    const { data } = await api.get('/agent-drafts/count');
    return data?.data?.count || 0;
  },
  async reject(ticketId: string): Promise<void> {
    await api.post(`/agent-drafts/${ticketId}/reject`);
  },
  async acknowledge(ticketId: string): Promise<void> {
    await api.post(`/agent-drafts/${ticketId}/acknowledge`);
  },
  async acknowledgeAll(): Promise<number> {
    const { data } = await api.post('/agent-drafts/acknowledge-manual');
    return data?.data?.count || 0;
  },
  async markSent(ticketId: string, opts?: { edited?: boolean; finalText?: string }): Promise<void> {
    await api.post(`/agent-drafts/${ticketId}/sent`, opts || {});
  },
  /** Taslağı gönder (mevcut reply endpoint) + 'sent' + öğrenme. İç iletişim taslaklarında ana ticket ekleri de gönderilir. */
  async send(ticketId: string, content: string, edited = false, attachmentIds?: string[]): Promise<void> {
    const body: Record<string, unknown> = { content, sendEmail: true, fromAgentDraft: true };
    if (attachmentIds && attachmentIds.length) body.attachment_ids = attachmentIds;
    await api.post(`/tickets/${ticketId}/reply`, body);
    await agentDraftsService.markSent(ticketId, { edited, finalText: content });
  },
  async metrics(): Promise<DraftMetrics> {
    const { data } = await api.get('/agent-drafts/metrics');
    return data?.data || { totals: {}, byConfidence: [], byCategory: [] };
  },
  async context(ticketId: string): Promise<DraftContext> {
    const { data } = await api.get(`/agent-drafts/${ticketId}/context`);
    return data?.data || { info: {}, conversation: [], orders: [] };
  },
};

export interface DraftContext {
  info: {
    ticketNumber?: string; subject?: string; status?: string; priority?: string; summary?: string | null;
    customerEmail?: string; customerName?: string; organizationName?: string; createdAt?: string;
  };
  conversation: { role: string; content: string; from?: string; timestamp?: string }[];
  orders: { source?: string; orderNumber?: string; date?: string; total?: string; status?: string;
    fulfillmentStatus?: string; shippedDate?: string | null; deliveryDate?: string | null; expectedDeliveryDate?: string | null;
    items?: { name?: string; quantity?: number; price?: string }[];
    returns?: { number?: string | null; date?: string | null; status?: string | null; trackingCode?: string | null; comment?: string | null }[] }[];
  notes?: { content: string; title?: string; isAgent: boolean; author?: string; createdAt?: string }[];
}

export interface DraftMetrics {
  totals: { sent?: number; sent_as_is?: number; sent_edited?: number; rejected?: number; manual?: number; pending?: number; open_tickets?: number };
  byConfidence: { confidence: string; sent: number; as_is: number; rejected: number }[];
  byCategory: { category: string; total: number; as_is: number }[];
}
